import { SelectionModel } from '@angular/cdk/collections';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import { cloneDeep, difference, findIndex, isEqual } from 'lodash-es';
import { EMPTY_ARRAY_LENGTH } from '../../../../../aumico-lib-core-fe/src/lib';
import { ColumnsNameMapper } from '../../mappers/data-grid/columnsName.mapper';
import { DataDeletionMapper } from '../../mappers/data-grid/dataDeletion.mapper';
import { IGridColumn } from './interfaces/IColumn.interface';
import { IDataGrid } from './interfaces/IDataGrid.interface';

const debug = Debug('aumico:templates:DataGridComponent');

export enum DataActions {
    DUPLICATE_ACCOUNT = 'DUPLICATE_ACCOUNT',
    UNSUMMARIZE_ACCOUNT = 'UNSUMMARIZE_ACCOUNT',
}

@UntilDestroy()
@Component({
    selector: 'data-grid',
    templateUrl: 'data-grid.component.html',
    styleUrls: ['data-grid.component.scss'],
})
export class DataGridComponent {
    tableColumns: IGridColumn[] = [];
    tableData: MatTableDataSource<object>;
    data: object[] = [];
    selection: SelectionModel<object>;
    headers: string[] = [];
    cellNameWithRemoveButton: string;
    allowSearch: boolean;
    allowSelection: boolean;
    allowDeleting: boolean;
    removalButtonText: string;
    removalButtonTextIndicator: string;
    selectColumnName: string /*= 'select' + Math.random().toString(36).substring(2)*/;
    readonly removeItemSvgUrl = './../../../../assets/icons/remove-item.svg';

    /**
     * REV-22122: optional test hooks — `testId` on the grid, and per row `rowTestIdPrefix` + the row's
     * `rowTestIdField` value. The account mapping uses them to expose its unmapped-accounts list
     * (`datev-unmapped-accounts` / `datev-unmapped-<accountNumber>`) without a second list.
     */
    @Input() testId?: string;
    @Input() rowTestIdPrefix?: string;
    @Input() rowTestIdField = 'id';

    @Input() set options(value: IDataGrid) {
        if (value != null) {
            this.updateDataSource(value);
        }
    }

    @Input() set removeButtonText(value: string) {
        if (value != null) {
            this.removalButtonText = value;
        }
    }

    @Output() actionEvent = new EventEmitter<{ eventType: DataActions; data: object }>();
    @Output() selectedRows = new EventEmitter<object[]>();
    @Output() deletedRows = new EventEmitter<object[]>();

    constructor(
        private columnsNameMapper: ColumnsNameMapper,
        private dataDeletionMapper: DataDeletionMapper,
        private i18nextService: I18NextService,
    ) {
        this.applyLocalizations();
    }

    private applyLocalizations(): void {
        this.removalButtonText = this.i18nextService.t('aumico_core_data_grid_items') as string;
        this.removalButtonTextIndicator = this.removalButtonText;
    }
    capitalize(columnsName: string): string {
        return columnsName[0].toUpperCase() + columnsName.slice(1);
    }

    isASelectColumn(columnName: string): boolean {
        return columnName.includes('select');
    }

    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.tableData.data.length;
        return numSelected === numRows;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    masterToggle() {
        if (this.isAllSelected()) {
            this.selection.clear();
            this.tableData.data.forEach((row: object) => (row['deletion'] = false));
        } else {
            // this is for the case where a filter is applied and all filtered results are selected
            if (this.tableData.filteredData.length !== 0) {
                this.tableData.filteredData.forEach((row: object) => this.selection.select(row));
                this.tableData.filteredData.forEach((row: object) => (row['deletion'] = true));
            } else {
                this.tableData.data.forEach((row: object) => this.selection.select(row));
                this.tableData.data.forEach((row: object) => (row['deletion'] = true));
            }
        }
        this.updateRemovalButtonText(this.selection.selected.length);
        this.selectedRows.emit(cloneDeep(this.dataDeletionMapper.deleteDeletionProperty(this.selection.selected)));
    }

    onDuplicateIconClick(row: object) {
        this.actionEvent.emit({ eventType: DataActions.DUPLICATE_ACCOUNT, data: cloneDeep(row) });

        // // emptying selected accounts so that they would not be mapped upon clicking on an fli
        this.clearSelection();
    }
    onUnsummarizedClick(row: object) {
        this.actionEvent.emit({ eventType: DataActions.UNSUMMARIZE_ACCOUNT, data: cloneDeep(row) });

        // // emptying selected accounts so that they would not be mapped upon clicking on an fli
        this.clearSelection();
    }

    clearSelection() {
        this.selection.clear();
        this.selectedRows.emit(cloneDeep(this.dataDeletionMapper.deleteDeletionProperty(this.selection.selected)));
    }

    selectRow(row: object) {
        this.selection.toggle(row);
        const rowIndex: number = findIndex(this.tableData.data, row);

        this.tableData.data[rowIndex]['deletion'] = !this.tableData.data[rowIndex]['deletion'];
        this.updateRemovalButtonText(this.selection.selected.length);
        this.selectedRows.emit(cloneDeep(this.dataDeletionMapper.deleteDeletionProperty(this.selection.selected)));

    }

    clickRow(row: object, event: PointerEvent) {
        if (event.shiftKey) {

            if (this.selection.selected.length === EMPTY_ARRAY_LENGTH) {
                return;
            }

            // HANDLE RANGE SELECTION
            const currentSelectionIndex = findIndex(this.tableData.data, row);
            const lastSelectionIndex = findIndex(this.tableData.data, this.selection.selected[this.selection.selected.length - 1]);

            const start = Math.min(currentSelectionIndex, lastSelectionIndex);
            const end = Math.max(currentSelectionIndex, lastSelectionIndex);

            for (let i = start + 1; i <= end - 1; i++) {
                if (this.selection.isSelected(this.tableData.data[i])) {
                    continue;
                }

                this.selectRow(this.tableData.data[i]);

            }
        }


    }

    removeRows() {
        if (this.selection.selected.length !== 0) {
            const subtractedUnSelectedRows: object[] = difference(this.tableData.data, this.selection.selected);
            this.tableData.data = subtractedUnSelectedRows;
            this.deletedRows.emit(cloneDeep(this.dataDeletionMapper.deleteDeletionProperty(this.selection.selected)));
            this.selection.clear();
            // we send length 0 because nothing is selected after removal
            this.updateRemovalButtonText(0);
        }
    }

    removeSpecificRow(row: object) {
        this.tableData.data = this.tableData.data.filter((tableRows: object) => !isEqual(tableRows, row));
        this.deletedRows.emit(cloneDeep(this.dataDeletionMapper.deleteDeletionProperty([row])));
        this.selection.deselect(row);
        // we send length 0 because nothing is selected after removal
        this.updateRemovalButtonText(0);
    }

    applyFilter(value: string) {
        this.tableData.filterPredicate = (data, filter) => {
            let filtrationResult = false;
            this.tableColumns.forEach((column) => {
                if (column.excludedFromSearch === false && column.matchInitials === true) {
                    // This filtration searches for values that START WITH the key
                    filtrationResult =
                        filtrationResult || this.isFilterValueMatchesCellInitials(data, column.dataField, filter);
                } else if (column.excludedFromSearch === false) {
                    // This filtration searches for values that INCLUDE the key
                    filtrationResult =
                        filtrationResult || this.isFilterValueMatchesCell(data, column.dataField, filter);
                }
            });
            return filtrationResult;
        };
        if (value == null) {
            return;
        }
        const filterValue = value;
        this.tableData.filter = filterValue.trim().toLowerCase();
    }

    private isFilterValueMatchesCellInitials(data: object, cell: string, filter: string): boolean {
        return data[cell].toString().trim().toLowerCase().startsWith(filter.trim().toLowerCase());
    }

    private isFilterValueMatchesCell(data: object, cell: string, filter: string): boolean {
        if (data[cell].toString().trim().toLowerCase().indexOf(filter.trim().toLowerCase()) !== -1) {
            return true;
        }
        return false;
    }

    public updateTableDataSource(dataSource: object[]) {
        this.tableData.data = cloneDeep(dataSource);
        this.selection = new SelectionModel<object>(true, []);
    }

    private updateDataSource(options: IDataGrid) {
        this.updateAllowances(options);
        this.selection = new SelectionModel<object>(true, []);
        this.tableColumns = cloneDeep(options.columns);
        debug(this.tableColumns);
        this.data = cloneDeep(this.dataDeletionMapper.addDeletionProperty(options.dataSource));
        this.cellNameWithRemoveButton =
            this.tableColumns.length > 0 ? this.tableColumns[this.tableColumns.length - 1].dataField : '';
        this.headers = cloneDeep(this.columnsNameMapper.mapColumnsName(options.columns));
        this.addSelectionColumn(options.allowSelection);
        this.tableData = new MatTableDataSource<object>(this.data);
    }

    private addSelectionColumn(allowSelection: boolean) {
        if (allowSelection) {
            this.selectColumnName = 'select' + Math.random().toString(36).substring(2);
            this.headers.unshift(this.selectColumnName);
            this.tableColumns.unshift({
                name: this.selectColumnName,
                dataField: null,
                dataType: null,
                nameType: 'string',
                excludedFromSearch: null,
            });
        }
    }

    private updateAllowances(options: IDataGrid) {
        this.allowSelection = options.allowSelection;
        this.allowSearch = options.allowSearch;
        this.allowDeleting = options.gridEditing.allowDeleting;
    }

    private updateRemovalButtonText(length: number) {
        if (length > 0) {
            if (length === this.tableData.data.length) {
                this.removalButtonTextIndicator =
                    this.i18nextService.t('aumico_core_data_grid_all') + ' ' + this.removalButtonText;
            } else {
                this.removalButtonTextIndicator = length.toString().concat(' ' + this.removalButtonText);
            }
        } else {
            this.removalButtonTextIndicator = this.removalButtonText;
        }
    }
}
